import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  X,
  Target,
  CheckCircle,
  Flag,
  BarChart4, 
  Calendar,
  FileText, 
  Users, 
  Plus, 
  Save, 
  Edit, 
  Activity, 
  Trash2,
  ArrowRight,
  TrendingUp,
  User,
  BookOpen,
  Star
} from 'lucide-react'
import { motion } from 'framer-motion'
import KPIAIAssistant from './KPIAIAssistant'
import EvidencePanel from './EvidencePanel'
import ProgressReviewCycles from './ProgressReviewCycles'
import { EvidenceEntry, AIInsight } from '@/types/objectives'
import { RoleKPI } from '@/services/api/performanceService'



interface KPIWorkspaceProps {
  kpi: RoleKPI
  onClose: () => void
  // Evidence Props
  evidence: EvidenceEntry[]
  onAddEvidence: (evidence: Omit<EvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => void
  // AI Props
  aiInsights: AIInsight[]
  isAILoading: boolean
  onGenerateInsights: (kpiId: string) => void
  // Milestone Props
  onAddMilestone: (milestone: {
    title: string
    description: string
    due_date: string
    milestone_type: string
  }) => void
  onUpdateMilestone: (milestoneId: string, milestone: {
    title?: string
    description?: string
    due_date?: string
    milestone_type?: string
    status?: string
    progress?: number
  }) => void
  onDeleteMilestone: (milestoneId: string) => void
}

export default function KPIWorkspace({
  kpi,
  onClose,
  evidence,
  onAddEvidence,
  aiInsights,
  isAILoading,
  onGenerateInsights,
  onAddMilestone,
  onUpdateMilestone,
  onDeleteMilestone
}: KPIWorkspaceProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const [showMilestoneForm, setShowMilestoneForm] = useState(false)
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    due_date: '',
    milestone_type: 'individual'
  })
  const [editingMilestone, setEditingMilestone] = useState<any>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)

  const getKPITypeBadge = (kpiType: string) => {
    switch (kpiType) {
      case 'strategic':
        return (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200 border text-sm px-3 py-1">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Strategic
            </div>
          </Badge>
        )
      case 'operational':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 border text-sm px-3 py-1">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Operational
            </div>
          </Badge>
        )
      case 'individual':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200 border text-sm px-3 py-1">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Individual
            </div>
          </Badge>
        )
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200 border text-sm px-3 py-1">
            <div className="flex items-center gap-2">
              <Flag className="h-4 w-4" />
              Unknown
            </div>
          </Badge>
        )
    }
  }

  const getMilestoneTypeBadge = (milestoneType: string) => {
    switch (milestoneType) {
      case 'strategic':
        return <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">Strategic</span>
      case 'operational':
        return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">Operational</span>
      case 'individual':
        return <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Individual</span>
      default:
        return <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-medium">Unknown</span>
    }
  }

  const calculateProgress = () => {
    return Math.round(((kpi as any).current_value / (kpi as any).target_value) * 100)
  }

  const getCompletedMilestones = () => {
    return kpi.milestones?.filter(m => m.status === 'completed').length || 0
  }

  const getOverdueMilestones = () => {
    return kpi.milestones?.filter(m => m.status === 'overdue').length || 0
  }

  const handleAddMilestone = () => {
    if (milestoneForm.title && milestoneForm.description && milestoneForm.due_date) {
      // Convert date to datetime format (add time if not present)
      const dueDate = milestoneForm.due_date.includes('T') 
        ? milestoneForm.due_date 
        : `${milestoneForm.due_date}T23:59:59`
      
      onAddMilestone({
        title: milestoneForm.title,
        description: milestoneForm.description,
        due_date: dueDate,
        milestone_type: milestoneForm.milestone_type
      })
      setMilestoneForm({
        title: '',
        description: '',
        due_date: '',
        milestone_type: 'individual'
      })
      setShowMilestoneForm(false)
    }
  }

  const handleEditMilestone = (milestone: any) => {
    setEditingMilestone(milestone)
    
    // Ensure proper date formatting for the input field
    let formattedDate = milestone.due_date
    if (formattedDate.includes('T')) {
      formattedDate = formattedDate.split('T')[0]
    }
    // Ensure it's in YYYY-MM-DD format
    if (formattedDate.includes('/')) {
      // Convert MM/DD/YYYY to YYYY-MM-DD if needed
      const parts = formattedDate.split('/')
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`
      }
    }
    
    setMilestoneForm({
      title: milestone.title,
      description: milestone.description || '',
      due_date: formattedDate, // Use properly formatted date
      milestone_type: milestone.milestone_type || 'individual'
    })
    setShowMilestoneForm(true)
  }

  const handleUpdateMilestone = () => {
    if (editingMilestone && milestoneForm.title && milestoneForm.description && milestoneForm.due_date) {
      // Store form values before reset
      const formData = {
        title: milestoneForm.title,
        description: milestoneForm.description,
        due_date: milestoneForm.due_date.includes('T') 
          ? milestoneForm.due_date 
          : `${milestoneForm.due_date}T23:59:59`,
        milestone_type: milestoneForm.milestone_type
      }
      
      // Reset form to prevent controlled/uncontrolled warnings
      resetMilestoneForm()
      
      // Update milestone with stored data
      onUpdateMilestone(editingMilestone.id, formData)
    }
  }

  const handleDeleteMilestone = (milestoneId: string) => {
    onDeleteMilestone(milestoneId)
    setShowDeleteConfirm(null)
  }

  const handleProgressUpdate = (milestoneId: string, progress: number) => {
    onUpdateMilestone(milestoneId, { progress })
  }

  const handleStatusUpdate = (milestoneId: string, status: string) => {
    onUpdateMilestone(milestoneId, { status })
  }

  const resetMilestoneForm = () => {
    setMilestoneForm({
      title: '',
      description: '',
      due_date: '',
      milestone_type: 'individual'
    })
    setEditingMilestone(null)
    setShowMilestoneForm(false)
  }

  const kpiRelatedEvidence = evidence

  return createPortal(
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-7xl h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header - Employee Performance Focus */}
        <div className="bg-gradient-to-br from-heritage-blue to-future-blue text-white p-6 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Target className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold">{kpi.name}</h1>
                    <Badge className="bg-white/20 text-white border-white/30">
                      Objective Workspace
                    </Badge>
                  </div>
                  <p className="text-blue-100 text-base leading-relaxed">{kpi.description}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 mt-4">
                {getKPITypeBadge((kpi as any).kpi_type || 'operational')}
                
                <div className="flex items-center gap-3 text-base font-medium">
                  <BarChart4 className="h-5 w-5" />
                  <span className="text-base font-medium">
                    {calculateProgress()}% Complete
                  </span>
                  <Progress value={calculateProgress()} className="w-32 h-3" />
                </div>

                {/* Evidence Count */}
                <div className="flex items-center gap-2 text-sm text-blue-100">
                  <FileText className="h-4 w-4" />
                  <span>{evidence.length} impact items</span>
                </div>

                {/* Next Review Indicator */}
                <div className="flex items-center gap-2 text-sm text-green-200">
                  <Calendar className="h-4 w-4" />
                  <span>Mid-year check-in: Jun 30</span>
                </div>

              </div>

              {/* Employee Context */}
              <div className="flex items-center gap-4 mt-4 text-sm text-blue-100">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>Owned by: {kpi.owner}</span>
                </div>
                <span>•</span>
                <span>{kpi.functional_area}</span>
                <span>•</span>
                <span>{(kpi as any).kpa_name || 'Unknown KPA'}</span>
                <ArrowRight className="h-4 w-4" />
                <span>{(kpi as any).pillar_name || 'No Strategic Pillar'}</span>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Employee-Focused Tabbed Content */}
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 rounded-none border-b bg-gray-50 h-auto p-0">
              <TabsTrigger value="overview" className="gap-2 h-16 px-4 py-3 data-[state=active]:bg-heritage-blue data-[state=active]:text-white transition-all duration-200">
                <div className="flex flex-col items-center gap-1">
                  <Target className="h-4 w-4" />
                  <span className="text-xs font-medium">Overview</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="evidence" className="gap-2 h-16 px-4 py-3 data-[state=active]:bg-heritage-blue data-[state=active]:text-white transition-all duration-200">
                <div className="flex flex-col items-center gap-1">
                  <FileText className="h-4 w-4" />
                  <span className="text-xs font-medium">Impact Portfolio</span>
                  <span className="text-xs opacity-75">({evidence.length})</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="milestones" className="gap-2 h-16 px-4 py-3 data-[state=active]:bg-heritage-blue data-[state=active]:text-white transition-all duration-200">
                <div className="flex flex-col items-center gap-1">
                  <Flag className="h-4 w-4" />
                  <span className="text-xs font-medium">Milestones</span>
                  <span className="text-xs opacity-75">({kpi.milestones?.length || 0})</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="reviews" className="gap-2 h-16 px-4 py-3 data-[state=active]:bg-heritage-blue data-[state=active]:text-white transition-all duration-200">
                <div className="flex flex-col items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs font-medium">Check-in Cycles</span>
                  <span className="text-xs opacity-75">Mid-year & Year-end</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="development" className="gap-2 h-16 px-4 py-3 data-[state=active]:bg-heritage-blue data-[state=active]:text-white transition-all duration-200">
                <div className="flex flex-col items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span className="text-xs font-medium">My Personalised Growth</span>
                  <span className="text-xs opacity-75">Learning &amp; growth actions</span>
                </div>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto">
              <TabsContent value="overview" className="p-6 m-0">
                <div className="space-y-8">
                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                    <Card className="border-l-4 border-l-heritage-blue">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-heritage-blue/10 rounded-lg">
                            <Target className="h-6 w-6 text-heritage-blue" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Current Value</p>
                            <p className="text-3xl font-bold text-heritage-blue">
                              {(kpi as any).current_value}<span className="text-sm font-normal text-gray-500">{(kpi as any).unit}</span>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-green-100 rounded-lg">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Progress</p>
                            <p className="text-3xl font-bold text-green-600">{calculateProgress()}%</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-blue-100 rounded-lg">
                            <Flag className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Milestones</p>
                            <p className="text-3xl font-bold text-blue-600">
                              {getCompletedMilestones()}/{kpi.milestones?.length || 0}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <FileText className="h-6 w-6 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Impact Items</p>
                            <p className="text-3xl font-bold text-purple-600">{kpiRelatedEvidence.length}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Strategic Context */}
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Target className="h-5 w-5 text-heritage-blue" />
                        Strategic Context
                      </h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Key Performance Area</label>
                            <p className="text-lg font-semibold text-gray-900">{(kpi as any).kpa_name}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Strategic Pillar</label>
                            <p className="text-sm text-muted-foreground">{(kpi as any).pillar_name || 'Unknown Pillar'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">KPI Type</label>
                            <div className="mt-1">
                              {getKPITypeBadge((kpi as any).kpi_type || 'operational')}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Functional Area</label>
                            <p className="text-lg font-semibold text-gray-900">{kpi.functional_area}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Frequency</label>
                            <p className="text-base text-gray-800 capitalize">
                              {(kpi as any).frequency || 'Monthly'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Milestones */}
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Flag className="h-5 w-5 text-blue-600" />
                        Recent Milestones
                      </h3>
                      <div className="space-y-3">
                        {(kpi.milestones || []).slice(0, 3).map((milestone) => (
                          <div key={milestone.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                            <div className={`w-3 h-3 rounded-full ${
                              milestone.status === 'completed' ? 'bg-emerald-500' :
                              milestone.status === 'in-progress' ? 'bg-blue-500' :
                              milestone.status === 'overdue' ? 'bg-red-500' :
                              'bg-gray-400'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900">{milestone.title}</h4>
                                {getMilestoneTypeBadge((milestone as any).milestone_type || 'operational')}
                              </div>
                              <p className="text-sm text-gray-600">Due: {new Date(milestone.due_date).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{milestone.progress || 0}%</span>
                              <Progress value={milestone.progress || 0} className="w-16 h-2" />
                            </div>
                          </div>
                        ))}
                      </div>
                      {(kpi.milestones?.length || 0) > 3 && (
                        <Button 
                          variant="ghost" 
                          className="mt-4 w-full"
                          onClick={() => setActiveTab('milestones')}
                        >
                          View All {kpi.milestones?.length || 0} Milestones
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="milestones" className="p-6 m-0">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Milestone Management</h2>
                    <div className="flex items-center gap-4">
                      {getOverdueMilestones() > 0 && (
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          {getOverdueMilestones()} Overdue
                        </Badge>
                      )}
                      <Button size="sm" onClick={() => setShowMilestoneForm(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Milestone
                      </Button>
                    </div>
                  </div>

                  {/* Add Milestone Form */}
                  {showMilestoneForm && (
                    <Card className="border-2 border-dashed border-heritage-blue/30">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetMilestoneForm}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title *
                            </label>
                            <input
                              type="text"
                              value={milestoneForm.title || ''}
                              onChange={(e) => setMilestoneForm({...milestoneForm, title: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue focus:border-heritage-blue"
                              placeholder="Enter milestone title"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description *
                            </label>
                            <textarea
                              value={milestoneForm.description || ''}
                              onChange={(e) => setMilestoneForm({...milestoneForm, description: e.target.value})}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue focus:border-heritage-blue resize-none"
                              placeholder="Describe what needs to be accomplished"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Due Date *
                              </label>
                              <input
                                type="date"
                                value={milestoneForm.due_date || ''}
                                onChange={(e) => setMilestoneForm({...milestoneForm, due_date: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue focus:border-heritage-blue"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Milestone Type
                              </label>
                              <select
                                value={milestoneForm.milestone_type || 'individual'}
                                onChange={(e) => setMilestoneForm({...milestoneForm, milestone_type: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue focus:border-heritage-blue"
                              >
                                <option value="individual">Individual</option>
                                <option value="operational">Operational</option>
                                <option value="strategic">Strategic</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 pt-4">
                            <Button 
                              onClick={editingMilestone ? handleUpdateMilestone : handleAddMilestone}
                              disabled={!milestoneForm.title || !milestoneForm.description || !milestoneForm.due_date}
                              className="flex items-center gap-2"
                            >
                              <Save className="h-4 w-4" />
                              {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
                            </Button>
                            <Button 
                              variant="outline" 
                              onClick={resetMilestoneForm}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid gap-4">
                    {(kpi.milestones || []).map((milestone) => (
                      <Card key={milestone.id} className={`border-l-4 ${
                        milestone.status === 'completed' ? 'border-l-emerald-500' :
                        milestone.status === 'in-progress' ? 'border-l-blue-500' :
                        milestone.status === 'overdue' ? 'border-l-red-500' :
                        'border-l-gray-300'
                      }`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">{milestone.title}</h3>
                                {getMilestoneTypeBadge((milestone as any).milestone_type || 'operational')}
                                <Badge className={`text-xs ${
                                  milestone.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                  milestone.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                                  milestone.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {milestone.status.replace('-', ' ')}
                                </Badge>
                              </div>
                              <p className="text-gray-600 mb-4">{milestone.description}</p>
                              <div className="flex items-center gap-6 text-sm text-gray-500">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  Due: {new Date(milestone.due_date).toLocaleDateString()}
                                </div>
                                <div className="flex items-center gap-2">
                                  <BarChart4 className="h-4 w-4" />
                                  Progress: {milestone.progress}%
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Progress:</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={milestone.progress || 0}
                                  onChange={(e) => handleProgressUpdate(milestone.id, Number(e.target.value))}
                                  className="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <span className="text-xs font-medium w-8">{milestone.progress || 0}%</span>
                              </div>
                              <Progress value={milestone.progress || 0} className="w-24 h-3" />
                              <div className="flex items-center gap-2">
                                <select
                                  value={milestone.status || 'pending'}
                                  onChange={(e) => handleStatusUpdate(milestone.id, e.target.value)}
                                  className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-heritage-blue"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="in-progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                  <option value="overdue">Overdue</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleEditMilestone(milestone)}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </Button>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={() => setShowDeleteConfirm(milestone.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Delete Confirmation Dialog */}
                  {showDeleteConfirm && createPortal(
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <Card className="w-full max-w-md mx-4">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full">
                              <Trash2 className="h-5 w-5 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Delete Milestone</h3>
                          </div>
                          <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this milestone? This action cannot be undone.
                          </p>
                          <div className="flex items-center gap-3 justify-end">
                            <Button 
                              variant="outline" 
                              onClick={() => setShowDeleteConfirm(null)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={() => handleDeleteMilestone(showDeleteConfirm)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>,
                    document.body
                  )}
                </div>
              </TabsContent>

              <TabsContent value="evidence" className="p-6 m-0">
                <EvidencePanel
                  objectiveId={kpi.id}
                  objectiveTitle={kpi.name}
                  evidence={kpiRelatedEvidence}
                  onAddEvidence={onAddEvidence}
                  initiatives={[]}
                  kpis={[]}
                  isCollapsed={false}
                />
              </TabsContent>

              <TabsContent value="reviews" className="p-6 m-0">
                <ProgressReviewCycles
                  objective={{
                    id: kpi.id,
                    title: kpi.name,
                    description: kpi.description,
                    status: kpi.status === 'on-track' ? 'active' : kpi.status === 'at-risk' ? 'on-hold' : 'cancelled',
                    priority: 'medium',
                    owner: kpi.owner,
                    kpa: (kpi as any).kpa_name || 'Unknown KPA',
                    pillar: (kpi as any).pillar_name || 'Unknown Pillar',
                    lastUpdated: new Date().toISOString()
                  } as any}
                  onUpdateObjective={(updates) => {
                    // In a real app, this would update the KPI via API
                    if (import.meta.env.DEV) console.log('Updating KPI:', updates)
                  }}
                />
              </TabsContent>

              <TabsContent value="ai" className="p-6 m-0">
                {/* <KPIAIAssistant
                  kpi={kpi}
                  onGenerateInsights={onGenerateInsights}
                  insights={aiInsights}
                  isLoading={isAILoading}
                /> */}
                <div className="text-center py-8 text-gray-500">
                  <p>AI Assistant features coming soon</p>
                </div>
              </TabsContent>

              <TabsContent value="development" className="p-6 m-0">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">My Personalised Growth</h2>
                      <p className="text-gray-600">Plan your career growth and skill development aligned with this objective</p>
                    </div>
                    <Button className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create My Personalised Growth Entry
                    </Button>
                  </div>

                  {/* Development Categories */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Current Role Enhancement */}
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Target className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Current Role</h3>
                            <p className="text-sm text-gray-600">Enhance impact & growth in existing position</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">Advanced Data Analysis</p>
                            <p className="text-xs text-blue-700">Excel & Power BI training</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-blue-600">Target: Q2 2024</span>
                              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">In Progress</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Future Role Preparation */}
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <ArrowRight className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Future Role</h3>
                            <p className="text-sm text-gray-600">Preparation for career advancement</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-purple-900">Leadership Skills</p>
                            <p className="text-xs text-purple-700">Team management & strategic thinking</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-purple-600">Target: Q3 2024</span>
                              <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">Planned</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Strategic Contribution */}
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Strategic Impact</h3>
                            <p className="text-sm text-gray-600">Alignment with organisational goals</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-green-900">Digital Innovation</p>
                            <p className="text-xs text-green-700">EdTech & online learning platforms</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-green-600">Target: Q4 2024</span>
                              <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">Scoped</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Development Interventions */}
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-4">Development Interventions & Timeline</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2">Development Need</th>
                              <th className="text-left py-2">Solution</th>
                              <th className="text-left py-2">Timeline</th>
                              <th className="text-left py-2">Milestones</th>
                              <th className="text-left py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            <tr>
                              <td className="py-3">Research Methodology</td>
                              <td className="py-3">Formal training program</td>
                              <td className="py-3">6 months</td>
                              <td className="py-3">Certification completion</td>
                              <td className="py-3">
                                <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>
                              </td>
                            </tr>
                            <tr>
                              <td className="py-3">Student Engagement Techniques</td>
                              <td className="py-3">Coaching & mentoring</td>
                              <td className="py-3">12 months</td>
                              <td className="py-3">360° feedback improvement</td>
                              <td className="py-3">
                                <Badge className="bg-amber-100 text-amber-800">Planned</Badge>
                              </td>
                            </tr>
                            <tr>
                              <td className="py-3">Cross-functional Knowledge</td>
                              <td className="py-3">Work assignments</td>
                              <td className="py-3">9 months</td>
                              <td className="py-3">Project deliverables</td>
                              <td className="py-3">
                                <Badge className="bg-green-100 text-green-800">Completed</Badge>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Coaching Support */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Users className="h-5 w-5 text-heritage-blue" />
                          Coaching Support
                        </h3>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="p-2 bg-heritage-blue/10 rounded-full">
                              <User className="h-4 w-4 text-heritage-blue" />
                            </div>
                            <div>
                              <p className="font-medium">People Leader Coaching</p>
                              <p className="text-sm text-gray-600">Dr. Sarah Mitchell - Weekly 1:1s</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="p-2 bg-purple-100 rounded-full">
                              <BookOpen className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">Subject Matter Expert</p>
                              <p className="text-sm text-gray-600">Prof. Alan Richards - Research Methods</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-heritage-blue" />
                          Learning Resources
                        </h3>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="p-2 bg-blue-100 rounded-full">
                              <FileText className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">Online Courses</p>
                              <p className="text-sm text-gray-600">Coursera, edX, LinkedIn Learning</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="p-2 bg-green-100 rounded-full">
                              <Star className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">Conferences</p>
                              <p className="text-sm text-gray-600">Industry events & knowledge sharing</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </motion.div>,
    document.body
  )
}
